import { getSocket } from 'socket';
import { NOT_CONNECTED_MESSAGE } from 'shared/lib/fetchSocket';

/** 서버의 Packing List 모듈. 세부 기능은 별도 소켓 이벤트가 아니라 payload.operation으로 고른다. */
export const PL_COMPONENT = 'il_packing_list';

const WRITE_ACTIONS = ['create', 'update', 'delete'];
const PL_TIMEOUT_MS = 20000;

const NO_RESPONSE_MESSAGE = 'The server did not respond. Try again — a repeated request is not applied twice.';

/** 서버 문구보다 담당자가 다음에 할 일을 알려줘야 하는 code만 바꿔 보여준다. */
const MESSAGE_BY_CODE = {
    // 배분 저장·별칭 등록처럼 서버에 아직 없는 operation. 서버 문구("Unknown operation …")는 담당자에게 오류처럼 읽힌다.
    OPERATION_UNKNOWN: 'This action is not available on the server yet.',
    VERSION_CONFLICT: 'This packing list was changed by someone else. Close it and open it again to see the latest version.',
    ALLOCATION_VERSION_CONFLICT: 'Allocations on this HBL were changed by someone else. The latest allocation has been loaded — check it and try again.',
};

/** 서버가 쓰기 요청마다 요구하는 요청 키. 서버는 같은 키로 이미 끝낸 요청을 다시 실행하지 않고 저장된 응답을 돌려준다. HTTP 업로드의 Idempotency-Key도 같은 모양이다. */
export const newCommandId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

/** 키 순서와 무관하게 같은 본문이면 같은 문자열이 되도록 직렬화한다. */
export const stableStringify = (value) => {
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.keys(value)
            .sort()
            .filter((k) => value[k] !== undefined)
            .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
            .join(',')}}`;
    }
    return JSON.stringify(value);
};

/**
 * 결과를 모르는 쓰기 요청의 command_id. 응답을 못 받았거나 서버가 아직 처리 중이라고 답한 요청이다.
 * 사용자가 같은 요청을 다시 보내면 이 id를 그대로 실어, 첫 요청이 저장됐다면 서버가 다시 실행하지 않고 그 결과를 돌려주게 한다
 * (수기 등록을 다시 눌러 문서가 둘 생기는 것을 막는다). 본문이 달라지면 다른 요청이라 새 id를 쓴다.
 */
const unsettledCommandIds = new Map();
const UNSETTLED_CODES = ['NO_RESPONSE', 'COMMAND_IN_PROGRESS'];

export const failure = (code, message, status = null) => ({ ok: false, status, code, message, data: null, details: [] });

/** 서버 ACK({ status, code, message, data, details })를 화면이 다루는 결과 하나로 바꾼다. */
export const interpretResponse = (response) => {
    if (!response || typeof response !== 'object') return failure('NO_RESPONSE', NO_RESPONSE_MESSAGE);
    const status = Number(response.status);
    return {
        ok: status >= 200 && status < 300,
        status,
        code: response.code ?? '',
        message: response.message ?? '',
        data: response.data ?? null,
        details: Array.isArray(response.details) ? response.details : [],
    };
};

export const isSuccess = (result) => result.ok;

/** 담당자에게 보여줄 한 줄. 422의 필드 오류는 메시지 뒤에 붙인다. */
export const describeResult = (result) => {
    const base = MESSAGE_BY_CODE[result.code] ?? (result.message || 'The request failed.');
    const fieldMessages = result.details.map((d) => d?.message).filter(Boolean);
    return fieldMessages.length ? `${base} ${fieldMessages.join(' ')}` : base;
};

/** Packing List 요청 한 건. scope는 { tab, shipmentId, houseId }이고 서버가 소속·권한을 다시 확인한다. 연결이 끊긴 동안에는 보내지 않는다. */
export function requestPackingList({ action, operation, scope, body = {} }) {
    const socket = getSocket();
    if (!socket?.connected) return Promise.resolve(failure('NOT_CONNECTED', NOT_CONNECTED_MESSAGE));
    const isWrite = WRITE_ACTIONS.includes(action);
    const requestKey = isWrite ? stableStringify({ action, operation, tab: scope.tab, shipmentId: scope.shipmentId, houseId: scope.houseId, body }) : null;
    const commandId = isWrite ? unsettledCommandIds.get(requestKey) ?? newCommandId() : null;
    const payload = {
        ...body,
        operation,
        tab: scope.tab,
        shipment_id: scope.shipmentId,
        house_id: scope.houseId,
        ...(commandId ? { command_id: commandId } : {}),
    };
    return new Promise((resolve) => {
        socket.timeout(PL_TIMEOUT_MS).emit(action, scope.tab, 'row', PL_COMPONENT, '', payload, (timeoutError, response) => {
            const result = interpretResponse(timeoutError ? null : response);
            // 응답 유실은 저장됐을 수도 있는 상태다. 담당자 문의를 추적할 수 있게 어느 요청이었는지 남긴다.
            if (timeoutError) console.warn('Packing list request timed out', { action, operation, command_id: commandId, house_id: scope.houseId });
            if (isWrite) {
                if (UNSETTLED_CODES.includes(result.code)) unsettledCommandIds.set(requestKey, commandId);
                else unsettledCommandIds.delete(requestKey);
            }
            resolve(result);
        });
    });
}
